import { useCallback } from 'react';

import { useFeedback } from '@/components/notifications/Feedback/useFeedback';
import { useReportError } from '@/hooks/useReportError';
import { useTrackUserEvent } from '@/hooks/useTrackUserEvent';
import {
  StartAiDispositiveProvisionWorkflowForEntitiesMutationHookResult,
  useStartAiDispositiveProvisionWorkflowForEntitiesMutation,
  useStartAiDispositiveProvisionWorkflowMutation,
} from '@/modules/aiDP/graphql/TriggerAIDPSuggestions.generated';
import { diagnostics } from '@/utils/diagnostics';

export enum TriggerDPAISuggestionsVariant {
  /**
   * Trigger the DP AI Suggestions workflow for 1 entity. This will cause one
   * notification to be added for the single entity's workflow progress.
   */
  SINGLE_ENTITY = 'SINGLE_ENTITY',
  /**
   * Trigger the DP AI Suggestions workflow for multiple entities within a
   * household. This will cause one notification to be added that tracks the
   * progress of the batch of entities.
   */
  BATCHED = 'BATCHED',
}

interface UseStartAIDPWorkflowOptions {
  variant: TriggerDPAISuggestionsVariant;
}

interface UseStartAIDPWorkflowResult {
  startWorkflow: ({
    householdId,
    entityId,
  }: {
    householdId: string | null;
    entityId?: string | null;
  }) => Promise<void>;
  isStartingWorkflow: boolean;
  startWorkflowForEntities: StartAiDispositiveProvisionWorkflowForEntitiesMutationHookResult[0];
  isStartingWorkflowForEntities: boolean;
}

/**
 * Hook to handle starting the AI DP workflow for extracting dispositions
 * from documents
 */
export function useStartAIDPWorkflow({
  variant,
}: UseStartAIDPWorkflowOptions): UseStartAIDPWorkflowResult {
  const { showFeedback } = useFeedback();
  const { reportError } = useReportError();

  const trackUserEvent = useTrackUserEvent();

  const [startWorkflowMutation, { loading: isStartingWorkflow }] =
    useStartAiDispositiveProvisionWorkflowMutation({
      onError: (error) => {
        showFeedback(
          'Error starting the extraction process. Please try again.'
        );
        reportError(
          'error in startAIDispositiveProvisionWorkflow mutation',
          error
        );
      },
      onCompleted: () => {
        // We could have a successful null response if there were no eligible
        // entities in the household for which to start the AIDPWorkflow
        // (e.g., household only has Business Entities)
        showFeedback('Successfully started data extraction', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        trackUserEvent('ai_dp start workflow clicked', {
          variant,
        });
      },
    });

  const startWorkflow = useCallback(
    async ({
      householdId,
      entityId,
    }: {
      householdId: string | null;
      entityId?: string | null;
    }) => {
      if (
        variant === TriggerDPAISuggestionsVariant.SINGLE_ENTITY &&
        (!entityId || !householdId)
      ) {
        diagnostics.error(
          `Entity ID and Household ID are required to start DP AI workflow for variant=${variant}`
        );
        return;
      }
      if (variant === TriggerDPAISuggestionsVariant.BATCHED && !householdId) {
        diagnostics.error(
          `Household ID is required to start DP AI workflow for variant=${variant}`
        );
        return;
      }

      // Create input object with required householdID
      const input: { householdID: string; entityID?: string } = {
        householdID: householdId!, // We've already checked householdID is not empty
      };

      // Only add entityID if it's not null
      if (entityId !== null) {
        input.entityID = entityId;
      }

      await startWorkflowMutation({
        variables: { input },
      });
    },
    [startWorkflowMutation, variant]
  );

  const [startWorkflowForEntities, { loading: isStartingWorkflowForEntities }] =
    useStartAiDispositiveProvisionWorkflowForEntitiesMutation({
      onError: (error) => {
        showFeedback(
          'Error starting the extraction process. Please try again.'
        );
        reportError(
          'error in startAIDispositiveProvisionWorkflowForEntities mutation',
          error
        );
      },
      onCompleted: () => {
        showFeedback('Successfully started data extraction', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        trackUserEvent('ai_dp start workflow clicked', {
          variant,
        });
      },
    });

  return {
    startWorkflow,
    isStartingWorkflow,
    startWorkflowForEntities,
    isStartingWorkflowForEntities,
  };
}
